/* eslint-disable react/jsx-no-literals */
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PRIVACY_FRAGMENT, TERMS_FRAGMENT } from './constants';
import { mailtoRichText } from './rich-text-values';

interface TermsSectionProps {
  accountName: string;
  supportContact: string;
}

export function TermsSection({
  accountName,
  supportContact,
}: TermsSectionProps) {
  const richText = mailtoRichText(supportContact);

  return (
    <section
      id={TERMS_FRAGMENT}
      aria-labelledby={`${TERMS_FRAGMENT}-heading`}
      data-testid="terms-of-service-section"
      className="mb-12"
    >
      <h2
        id={`${TERMS_FRAGMENT}-heading`}
        className="mb-4 text-2xl font-semibold"
      >
        <FormattedMessage
          defaultMessage="Terms of Service"
          description="Heading for the Terms of Service section of the career site privacy page"
        />
      </h2>

      <p>
        <FormattedMessage
          defaultMessage="By using the site operated by {accountName} (“we,” “us,” or “our”), you agree to these Terms of Service."
          description="Terms of service intro paragraph"
          values={{ accountName }}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="1. Use of This Site"
          description="Terms of service section 1 heading: Use of This Site"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="This site is operated by {accountName} to facilitate job applications and employment-related communications. You agree to use this site only for lawful purposes and in accordance with these Terms."
          description="Terms of service section 1 description of permitted use"
          values={{ accountName }}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="2. SMS Messaging Program"
          description="Terms of service section 2 heading: SMS Messaging Program"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="By checking the opt-in box on our application form, you agree to receive SMS text messages from {accountName} at the phone number you provided. Messages may include:"
          description="Terms of service section 2 SMS opt-in description"
          values={{ accountName }}
        />
      </p>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="Application status updates, onboarding information, and other employment-related notifications (transactional)"
            description="Terms of service section 2 message type: transactional"
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="Job opportunities and positions you may be eligible for (marketing)"
            description="Terms of service section 2 message type: marketing"
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="Message frequency varies. Message and data rates may apply."
          description="Terms of service section 2 message frequency disclaimer"
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="Consent to receive SMS messages is not a condition of applying for or obtaining employment."
          description="Terms of service section 2 voluntariness statement"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="3. How to Get Help"
          description="Terms of service section 3 heading: How to Get Help"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="Contact us at: <supportLink>{supportContact}</supportLink>"
          description="Terms of service section 3 contact instructions"
          values={{ supportContact, ...richText }}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="4. How to Opt Out"
          description="Terms of service section 4 heading: How to Opt Out"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="Reply STOP to any text message we send you to unsubscribe."
          description="Terms of service section 4 STOP instruction"
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="After opting out, you will receive one confirmation message. You can re-subscribe by re-enrolling through the career site or by texting START in reply to any of our messages."
          description="Terms of service section 4 re-subscribe instructions"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="5. Disclaimer of Warranties"
          description="Terms of service section 5 heading: Disclaimer of Warranties"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage={
            'This site and its contents are provided “as is” without warranties of any kind, ' +
            'express or implied. We do not warrant that the site will be uninterrupted or error-free.'
          }
          description="Terms of service section 5 warranty disclaimer"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="6. Limitation of Liability"
          description="Terms of service section 6 heading: Limitation of Liability"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="To the fullest extent permitted by applicable law, {accountName} shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of this site or our SMS program."
          description="Terms of service section 6 liability limitation"
          values={{ accountName }}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="7. Governing Law"
          description="Terms of service section 7 heading: Governing Law"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="These Terms are governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state and federal courts located in Delaware."
          description="Terms of service section 7 governing law statement"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="8. Changes to These Terms"
          description="Terms of service section 8 heading: Changes to These Terms"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="We may update these Terms periodically. Continued use of this site after changes are posted constitutes your acceptance of the revised Terms."
          description="Terms of service section 8 update policy"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="9. Contact Us"
          description="Terms of service section 9 heading: Contact Us"
        />
      </h3>
      <p>
        <strong>{accountName}</strong>
      </p>
      <p>
        <FormattedMessage
          defaultMessage="<supportLink>{supportContact}</supportLink>"
          description="Terms of service section 9 contact email"
          values={{ supportContact, ...richText }}
        />
      </p>
      <p>
        <a href={`#${PRIVACY_FRAGMENT}`} className="underline">
          <FormattedMessage
            defaultMessage="Privacy Policy"
            description="Terms of service section 9 link back to the Privacy Policy section"
          />
        </a>
      </p>
    </section>
  );
}
